/**
 * Background Jobs Store
 *
 * Zustand store for tracking background jobs (imports, evaluations, etc.)
 * from the frontend. Works with the BackgroundJobsPanel component
 * and the GET /api/jobs endpoint.
 */

import { create } from "zustand"

export type JobStatus = "pending" | "processing" | "completed" | "failed" | "cancelled"
export type JobType = "tender_import" | "evaluation" | "comparison" | "pliego_generation" | "section_generation" | "section_extraction" | "specs_analysis" | "template_decision_analysis" | "document_review" | "document_ocr" | "criteria_extraction" | "criteria_enrichment" | "objective_suggest"

export interface BackgroundJob {
	id: string
	type: JobType
	status: JobStatus
	progress: number
	currentStep: string | null
	title: string | null
	subtitle: string | null
	input?: unknown
	result: unknown
	errorMessage: string | null
	createdAt: string
	startedAt: string | null
	completedAt: string | null
}

/**
 * Jobs the user never asked to watch: section extraction fills the document
 * editor of an expedient in the background and blocks nothing (evaluation
 * proceeds without it), so its card only confused people into waiting for it
 * (BUG-139). Template extractions keep their aggregate card; a failed job
 * stays visible so it can be retried.
 */
export function isSilentJob(job: Pick<BackgroundJob, "type" | "input" | "status">): boolean {
	// Criteria enrichment only fills empty "què demana el plec" fields after an
	// import; the criteria are usable without it, so it gets no card either.
	if (job.type === "criteria_enrichment") return job.status !== "failed" && job.status !== "cancelled"
	// The editor's implicit criteria refresh after saving a criteria clause:
	// the user asked for nothing, so only a failure gets a card.
	if (job.type === "criteria_extraction") {
		if ((job.input as { trigger?: unknown } | null)?.trigger !== "section_save") return false
		return job.status !== "failed" && job.status !== "cancelled"
	}
	if (job.type !== "section_extraction") return false
	if ((job.input as { targetType?: unknown } | null)?.targetType === "template") return false
	return job.status !== "failed" && job.status !== "cancelled"
}

interface BackgroundJobsState {
	jobs: BackgroundJob[]
	isMinimized: boolean
	/**
	 * True once the "what is running right now?" fetch has answered (successfully
	 * or not). Until then `jobs` is an empty array that means "not loaded yet",
	 * NOT "nothing is running" — anything that reacts to the ABSENCE of a job has
	 * to wait for this, or it acts on a false negative during the first seconds
	 * after a page load.
	 */
	jobsHydrated: boolean

	// Actions
	setJobs: (jobs: BackgroundJob[]) => void
	markJobsHydrated: () => void
	addJob: (job: BackgroundJob) => void
	updateJob: (id: string, data: Partial<BackgroundJob>) => void
	removeJob: (id: string) => void
	clearCompleted: () => void
	toggleMinimized: () => void

	// Computed helpers
	hasActiveJobs: () => boolean
	activeJobCount: () => number
}

export const useBackgroundJobsStore = create<BackgroundJobsState>()((set, get) => ({
	jobs: [],
	isMinimized: false,
	jobsHydrated: false,

	setJobs: (jobs) => set({ jobs, jobsHydrated: true }),

	markJobsHydrated: () => set({ jobsHydrated: true }),

	addJob: (job) =>
		set((state) => ({
			jobs: [job, ...state.jobs.filter((j) => j.id !== job.id)],
			// Auto-expand when a new job is added, unless nobody will see it.
			isMinimized: isSilentJob(job) ? state.isMinimized : false,
		})),

	updateJob: (id, data) =>
		set((state) => ({
			jobs: state.jobs.map((job) => {
				if (job.id !== id) return job
				const next = { ...job, ...data }
				// The panel auto-dismisses completed cards 60s after completion:
				// make sure every completed job carries a completion timestamp,
				// even when the server payload omitted it.
				if (next.status === "completed" && !next.completedAt) {
					next.completedAt = new Date().toISOString()
				}
				return next
			}),
		})),

	removeJob: (id) =>
		set((state) => ({
			jobs: state.jobs.filter((job) => job.id !== id),
		})),

	clearCompleted: () =>
		set((state) => ({
			jobs: state.jobs.filter(
				(job) => job.status !== "completed" && job.status !== "failed" && job.status !== "cancelled"
			),
		})),

	toggleMinimized: () =>
		set((state) => ({ isMinimized: !state.isMinimized })),

	hasActiveJobs: () => {
		const { jobs } = get()
		return jobs.some((job) => job.status === "pending" || job.status === "processing")
	},

	activeJobCount: () => {
		const { jobs } = get()
		return jobs.filter((job) => job.status === "pending" || job.status === "processing").length
	},
}))
